import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Landmark, Gavel } from "lucide-react";

export const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const daysUntil = (d) => {
    if (!d) return null;
    const dt = new Date(`${String(d).slice(0, 10)}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((dt - today) / 86400000);
};

export function AuctionStatusPill({ item, className = "" }) {
    const closed = item.auction_status === "closed";
    const days = daysUntil(item.auction_date);
    const label = closed ? "Auction Closed" : days === 0 ? "Auction Today" : days > 0 ? `Upcoming · ${days} day${days === 1 ? "" : "s"} left` : "Upcoming";
    return (
        <span
            data-testid={`auction-status-${item.slug}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-bold ${closed ? "bg-black/80 text-white/80" : "bg-emerald-600 text-white"} ${className}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${closed ? "bg-white/50" : "bg-white animate-pulse"}`}/>
            {label}
        </span>
    );
}

export function SampleTag({ show, className = "" }) {
    if (!show) return null;
    return (
        <span data-testid="sample-tag" className={`inline-flex items-center px-2 py-1 text-[9px] uppercase tracking-[0.2em] font-bold bg-white text-black border border-black/15 ${className}`}>
            Sample
        </span>
    );
}

export default function AuctionCard({ item }) {
    const closed = item.auction_status === "closed";
    return (
        <Link
            to={`/properties/${item.slug}`}
            data-testid={`auction-card-${item.slug}`}
            className={`group block bg-white border border-black/8 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.35)] transition-shadow duration-500 ${closed ? "opacity-80" : ""}`}
        >
            <div className="relative aspect-[5/4] overflow-hidden bg-black/5">
                <img src={item.cover_image} alt={item.title} loading="lazy" className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${closed ? "grayscale" : ""}`}/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"/>
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 bg-[var(--ebg-gold)] text-black px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] font-bold">
                        <Gavel size={11}/> Auction
                    </span>
                    <SampleTag show={item.is_sample}/>
                </div>
                <div className="absolute bottom-3 left-3"><AuctionStatusPill item={item}/></div>
            </div>
            <div className="p-5">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--ebg-gold)]">{item.auction_type || "Bank Auction"}</div>
                <h3 className="font-serif text-[21px] leading-tight text-black mt-1.5">{item.title}</h3>
                <p className="text-black/55 text-sm mt-1">{item.locality}, {item.city}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
                    <div className="flex items-start gap-2"><CalendarDays size={13} className="text-[var(--ebg-gold)] mt-0.5 shrink-0"/><div><div className="text-black/45 text-[10px] uppercase tracking-[0.16em]">Auction Date</div><div className="font-bold">{fmtDate(item.auction_date)}</div></div></div>
                    <div className="flex items-start gap-2"><Landmark size={13} className="text-[var(--ebg-gold)] mt-0.5 shrink-0"/><div className="min-w-0"><div className="text-black/45 text-[10px] uppercase tracking-[0.16em]">Authority</div><div className="font-bold truncate">{item.auction_authority || "—"}</div></div></div>
                </div>
                <div className="mt-4 pt-4 border-t border-black/8 flex items-end justify-between gap-3">
                    <div>
                        <div className="text-[9px] uppercase tracking-[0.22em] text-black/45 font-bold">Reserve Price</div>
                        <div className="font-serif text-[22px] text-black tabular-nums" data-testid={`auction-price-${item.slug}`}>{item.price_display}</div>
                        {item.auction_emd && <div className="text-[11px] text-black/50">EMD {item.auction_emd}</div>}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--ebg-gold)] border border-[var(--ebg-gold)]/50 px-3 py-2 group-hover:bg-[var(--ebg-gold)] group-hover:text-black transition-colors inline-flex items-center gap-1.5 shrink-0">
                        View Details <ArrowRight size={11}/>
                    </span>
                </div>
            </div>
        </Link>
    );
}
